import { CALENDAR_CONFIG } from '../../config/constants.js'
import { DEFAULT_UI_LOCALE, type UiLocale } from '../../core/contracts.js'
import type { TranscriptItem } from '../../types/visual.js'
import { logger } from '../../lib/logger.js'
import enChat from '../../i18n/locales/en/chat.json' with { type: 'json' }
import nbChat from '../../i18n/locales/nb/chat.json' with { type: 'json' }
import { DISCOVERY_REPORT_ACK_TEXT } from '../../../utils/discoveryReportUtils.js'

export { DISCOVERY_REPORT_ACK_TEXT }

/** Same strings as chat.json `report.defaultName`. The card regex only rewrites names already stored. */
const REPORT_NAME_BY_LOCALE: Record<UiLocale, string> = {
  en: enChat.report.defaultName,
  nb: nbChat.report.defaultName,
}

function defaultReportName(locale: UiLocale): string {
  return REPORT_NAME_BY_LOCALE[locale]
}

const DEFAULT_BOOKING_URL = CALENDAR_CONFIG.DEFAULT_LINK

export function isDiscoveryReportHtml(text: string | undefined): boolean {
  if (!text?.trim()) return false
  const trimmed = text.trim()
  if (/^<!DOCTYPE\s+html/i.test(trimmed)) return true
  if (/<html[\s>]/i.test(trimmed) && /STRATEGIC BRIEF|AI Transformation Proposal|AI Insights Report|class=["']page["']/i.test(trimmed)) {
    return true
  }
  return false
}

function extractReportNameFromHtml(html: string, locale: UiLocale): string {
  const name = defaultReportName(locale)
  const titleMatch = /<title[^>]*>([^<]+)<\/title>/i.exec(html)
  const rawTitle = titleMatch?.[1]?.trim()
  if (!rawTitle) return name
  return rawTitle.replace(/^STRATEGIC BRIEF\s*-\s*/i, `${name} - `).trim()
    .replace(/^AI Insights Report\s*-\s*/i, `${name} - `)
    .trim()
}

function logReportNormalize(
  item: TranscriptItem,
  reason: string,
  normalized: TranscriptItem
): void {
  logger.debug('[future-chat] discovery report message normalized', {
    reason,
    messageId: item.id,
    bodyPreview: item.text?.slice(0, 200),
    attachmentType: item.attachment?.type,
    hadHtmlInBody: isDiscoveryReportHtml(item.text),
    hadHtmlInAttachmentData: isDiscoveryReportHtml(item.attachment?.data),
    hadHtmlContent: Boolean(item.attachment?.htmlContent),
    normalizedAttachmentType: normalized.attachment?.type,
    normalizedHasHtmlContent: Boolean(normalized.attachment?.htmlContent),
  })
}

/**
 * Ensures discovery report HTML lives only on attachment.htmlContent — never in message.body.
 */
export function normalizeDiscoveryReportTranscriptItem(
  item: TranscriptItem,
  locale: UiLocale = DEFAULT_UI_LOCALE,
): TranscriptItem {
  const reportName = defaultReportName(locale)
  const attachment = item.attachment
  const htmlInBody = isDiscoveryReportHtml(item.text)
  const htmlInData = isDiscoveryReportHtml(attachment?.data)

  if (attachment?.type === 'discovery_report') {
    const htmlContent =
      attachment.htmlContent ||
      (htmlInData ? attachment.data : undefined) ||
      (htmlInBody ? item.text : undefined)

    if (!htmlContent) {
      if (htmlInBody) {
        const normalized: TranscriptItem = {
          ...item,
          text: DISCOVERY_REPORT_ACK_TEXT,
          attachment: {
            ...attachment,
            htmlContent: item.text,
            url: attachment.url || DEFAULT_BOOKING_URL,
            name: attachment.name || reportName,
          },
        }
        logReportNormalize(item, 'discovery_report_missing_htmlContent', normalized)
        return normalized
      }
      return item
    }

    const { data: legacyData, ...attachmentWithoutData } = attachment
    const normalized: TranscriptItem = {
      ...item,
      text: htmlInBody ? DISCOVERY_REPORT_ACK_TEXT : item.text,
      attachment: {
        ...attachmentWithoutData,
        htmlContent,
        url: attachment.url || DEFAULT_BOOKING_URL,
        name: attachment.name || extractReportNameFromHtml(htmlContent, locale),
        ...(legacyData && !htmlInData ? { data: legacyData } : {}),
      },
    }

    if (
      htmlInBody ||
      htmlInData ||
      !attachment.htmlContent ||
      item.text !== normalized.text
    ) {
      logReportNormalize(item, 'discovery_report_attachment_repair', normalized)
    }
    return normalized
  }

  if (item.role !== 'model') return item

  if (htmlInBody) {
    const htmlContent = item.text
    const normalized: TranscriptItem = {
      ...item,
      text: DISCOVERY_REPORT_ACK_TEXT,
      attachment: {
        type: 'discovery_report',
        name: extractReportNameFromHtml(htmlContent, locale),
        htmlContent,
        url: DEFAULT_BOOKING_URL,
      },
    }
    logReportNormalize(item, 'body_html_to_discovery_report', normalized)
    return normalized
  }

  if (htmlInData && attachment) {
    const normalized: TranscriptItem = {
      ...item,
      text: DISCOVERY_REPORT_ACK_TEXT,
      attachment: {
        ...attachment,
        type: 'discovery_report',
        htmlContent: attachment.data!,
        url: attachment.url || DEFAULT_BOOKING_URL,
        name: attachment.name || reportName,
      },
    }
    logReportNormalize(item, 'attachment_data_html_to_discovery_report', normalized)
    return normalized
  }

  return item
}
